import { Group } from "@mantine/core"
import { useEffect, useState } from "react"
import z from "zod"

import {
  DateFilterDto,
  dateFilterDtoSchema,
  DateFilterOperator,
  DateFilterType,
  SlidingPreset,
} from "@tfg/types/types/date-filter.dto"

import { Select } from "../../Inputs/Select/Select"

import { useDatePickerComponent } from "./hooks/useDateFilterValueComponentFactory"
import { useOperatorsSelectItems } from "./hooks/useOperatorsSelectItems"
import { useSlidingPresetsSelectItems } from "./hooks/useSlidingPresetsSelectItems"

export type DateFilterProps = {
  key: string
  onChange: (filter: Partial<DateFilterDto>) => void
  locale?: string
  value: DateFilterDto | null
  operatorLabelTranslationMapping: Record<DateFilterOperator, string>
  slidingPresetLabelTranslationMapping: Record<SlidingPreset, string>
}

export const DateFilter = ({
  locale,
  onChange,
  value,
  operatorLabelTranslationMapping,
  slidingPresetLabelTranslationMapping,
}: DateFilterProps) => {
  const [dateFilter, setDateFilter] = useState<DateFilterDto>(
    value || {
      operator: DateFilterOperator.EQUALS,
      type: DateFilterType.SLIDER,
      preset: SlidingPreset.TODAY,
    }
  )

  const datePickerComponent = useDatePickerComponent({
    dateFilter,
    locale,
    onChange: (range) =>
      setDateFilter({
        ...dateFilter,
        range: {
          startDate: range[0],
          endDate: range[1],
        },
      }),
  })

  const operatorSelectItems = useOperatorsSelectItems(
    operatorLabelTranslationMapping,
    dateFilter.type
  )
  const slidingPresetSelectItems = useSlidingPresetsSelectItems(
    slidingPresetLabelTranslationMapping,
    dateFilter.operator
  )

  useEffect(() => {
    const { data, success } = dateFilterDtoSchema.safeParse(dateFilter)
    if (success) {
      onChange(data)
    }
  }, [dateFilter])

  return (
    <Group gap="xxs">
      <Select
        data={operatorSelectItems}
        value={dateFilter.operator}
        onChange={(selectedOperator) =>
          setDateFilter({
            ...dateFilter,
            operator: z.enum(DateFilterOperator).parse(selectedOperator),
          })
        }
        maw="7rem"
      />
      {![DateFilterOperator.IS_EMPTY, DateFilterOperator.IS_NOT_EMPTY].includes(
        dateFilter.operator
      ) ? (
        <Select
          data={slidingPresetSelectItems}
          value={dateFilter.preset}
          onChange={(selectedPreset) => {
            const parsedPreset = z.enum(SlidingPreset).parse(selectedPreset)
            setDateFilter({
              ...dateFilter,
              preset: parsedPreset,
              type:
                parsedPreset === SlidingPreset.CUSTOM
                  ? DateFilterType.FIXED
                  : DateFilterType.SLIDER,
            })
          }}
          w="8rem"
        />
      ) : null}
      {datePickerComponent}
    </Group>
  )
}
